import { useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import { downloadCsv as download } from "./lib/csv";

type Visit = { date: string; time: string; endDate?: string; nights?: number; experience: string; partySize: number; status: "pending" | "confirmed" | "cancelled"; source: string; notes: string; createdAt: string; details?: string };
type Customer = {
  key: string; firstName: string; lastName: string; phone: string; email: string;
  bookings: number; cancelled: number; people: number; firstDate: string; lastDate: string;
  experiences: string[]; newsletter: boolean; history: Visit[];
};
type Result = { customers: Customer[]; summary: { total: number; returning: number; newsletter: number; bookings: number } };

const STATUS = { pending: "Da confermare", confirmed: "Confermata", cancelled: "Annullata" } as const;
const SOURCE: Record<string, string> = { web: "Sito", whatsapp: "WhatsApp", manual: "Inserita a mano" };

function it(date: string) {
  const [y, m, d] = date.split("-");
  return d && m && y ? `${d}/${m}/${y}` : date;
}

function stamp() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

const LABELS = {
  escape: { place: "Esperienze", unit1: "prenotazione", unitN: "prenotazioni", people: "Persone portate", newsletter: true, allPlaces: "Tutte le esperienze", first: "Prima volta" },
  rental: { place: "Case", unit1: "soggiorno", unitN: "soggiorni", people: "Ospiti totali", newsletter: false, allPlaces: "Tutte le case", first: "Primo soggiorno" },
} as const;

export default function CustomerBook({ endpoint, canEdit, mode }: { endpoint: string; canEdit: boolean; mode: "escape" | "rental" }) {
  const L = LABELS[mode];
  const [data, setData] = useState<Result | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [experience, setExperience] = useState("");
  const [open, setOpen] = useState("");

  useEffect(() => {
    let active = true;
    api<Result>(endpoint)
      .then((result) => { if (active) setData(result); })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : "Clienti non disponibili"); });
    return () => { active = false; };
  }, [endpoint]);

  const experiences = useMemo(() => [...new Set((data?.customers || []).flatMap((customer) => customer.experiences))].sort(), [data]);
  const list = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const digits = needle.replace(/\D/g, "");
    return (data?.customers || []).filter((customer) => {
      if (experience && !customer.experiences.includes(experience)) return false;
      if (!needle) return true;
      return `${customer.firstName} ${customer.lastName} ${customer.email}`.toLowerCase().includes(needle) || (digits.length >= 3 && customer.phone.replace(/\D/g, "").includes(digits));
    });
  }, [data, query, experience]);

  function exportCustomers(rows: Customer[], name: string) {
    if (mode === "rental") {
      download(name, [
        ["Nome", "Telefono", "Email", "Soggiorni", "Annullati", "Ospiti totali", "Primo soggiorno", "Ultimo soggiorno", "Case"],
        ...rows.map((c) => [`${c.firstName} ${c.lastName}`.trim(), c.phone, c.email, c.bookings, c.cancelled, c.people, it(c.firstDate), it(c.lastDate), c.experiences.join(" + ")]),
      ]);
      return;
    }
    download(name, [
      ["Nome", "Cognome", "Telefono", "Email", "Prenotazioni", "Annullate", "Persone totali", "Prima prenotazione", "Ultima prenotazione", "Esperienze", "Iscritto newsletter"],
      ...rows.map((c) => [c.firstName, c.lastName, c.phone, c.email, c.bookings, c.cancelled, c.people, it(c.firstDate), it(c.lastDate), c.experiences.join(" + "), c.newsletter ? "Sì" : "No"]),
    ]);
  }
  function exportBookings(rows: Customer[]) {
    const all = rows.flatMap((c) => c.history.map((v) => ({ c, v }))).sort((a, b) => (b.v.date + b.v.time).localeCompare(a.v.date + a.v.time));
    if (mode === "rental") {
      download(`soggiorni-${stamp()}.csv`, [
        ["Casa", "Check-in", "Check-out", "Notti", "Ospiti", "Nome", "Telefono", "Email", "Stato", "Origine", "Dettagli", "Note", "Ricevuta il"],
        ...all.map(({ c, v }) => [v.experience, it(v.date), v.endDate ? it(v.endDate) : "", v.nights ?? "", v.partySize, `${c.firstName} ${c.lastName}`.trim(), c.phone, c.email, STATUS[v.status], SOURCE[v.source] || v.source, v.details || "", v.notes, new Date(v.createdAt).toLocaleString("it-IT", { timeZone: "Europe/Rome" })]),
      ]);
      return;
    }
    download(`prenotazioni-${stamp()}.csv`, [
      ["Data", "Ora", "Esperienza", "Nome", "Cognome", "Telefono", "Email", "Persone", "Stato", "Origine", "Note", "Ricevuta il"],
      ...all.map(({ c, v }) => [it(v.date), v.time, v.experience, c.firstName, c.lastName, c.phone, c.email, v.partySize, STATUS[v.status], SOURCE[v.source] || v.source, v.notes, new Date(v.createdAt).toLocaleString("it-IT", { timeZone: "Europe/Rome" })]),
    ]);
  }

  if (!data) return <div className="escape-empty">{error || "Caricamento dei clienti…"}</div>;
  const filtered = Boolean(query.trim() || experience);

  return <div className="cust">
    <div className="cust-stats">
      <div><strong>{data.summary.total}</strong><span>Clienti</span></div>
      <div><strong>{data.summary.bookings}</strong><span>{mode === "rental" ? "Soggiorni" : "Prenotazioni"}</span></div>
      <div><strong>{data.summary.returning}</strong><span>Tornati più volte</span></div>
      {L.newsletter && <div><strong>{data.summary.newsletter}</strong><span>Iscritti alla newsletter</span></div>}
    </div>

    <div className="cust-tools">
      <input type="search" placeholder="Cerca per nome, telefono o email" value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Cerca un cliente" />
      <select value={experience} onChange={(event) => setExperience(event.target.value)} aria-label="Filtra">
        <option value="">{L.allPlaces}</option>
        {experiences.map((name) => <option key={name} value={name}>{name}</option>)}
      </select>
    </div>
    <div className="cust-export">
      <button type="button" className="wiz-primary" disabled={!canEdit || !list.length} onClick={() => exportCustomers(list, `clienti-${stamp()}.csv`)}>⬇ Scarica clienti (CSV){filtered ? ` · ${list.length} filtrati` : ""}</button>
      <button type="button" disabled={!canEdit || !list.length} onClick={() => exportBookings(list)}>⬇ Scarica {mode === "rental" ? "soggiorni" : "prenotazioni"} (CSV)</button>
    </div>
    <p className="cust-note">I file si aprono con Excel e Google Fogli. I dati servono per gestire le prenotazioni{L.newsletter ? ": per mandare promozioni usa solo chi è iscritto alla newsletter" : ", non per promozioni senza consenso"}.</p>

    {list.length === 0 && <div className="agenda-empty">{data.summary.total ? "Nessun cliente corrisponde alla ricerca." : mode === "rental" ? "Ancora nessuna richiesta: gli ospiti compariranno qui appena arrivano." : "Ancora nessuna prenotazione: i clienti compariranno qui appena arrivano le prime richieste."}</div>}
    {list.map((customer) => <article className={`cust-card${open === customer.key ? " is-open" : ""}`} key={customer.key}>
      <button type="button" className="cust-row" aria-expanded={open === customer.key} onClick={() => setOpen(open === customer.key ? "" : customer.key)}>
        <div><strong>{customer.firstName} {customer.lastName}</strong><small>{customer.phone}{customer.email ? ` · ${customer.email}` : ""}</small></div>
        <div className="cust-meta"><span>{customer.bookings} {customer.bookings === 1 ? L.unit1 : L.unitN}</span><small>ultima: {it(customer.lastDate)}</small></div>
        {customer.bookings > 1 && <em className="cust-tag">Cliente abituale</em>}
      </button>
      {open === customer.key && <div className="cust-detail">
        <div className="cust-facts">
          <span><b>Telefono</b><a href={`tel:${customer.phone}`}>{customer.phone}</a></span>
          {customer.email && <span><b>Email</b><a href={`mailto:${customer.email}`}>{customer.email}</a></span>}
          <span><b>{L.people}</b>{customer.people}</span>
          <span><b>{L.first}</b>{it(customer.firstDate)}</span>
          <span><b>{L.place}</b>{customer.experiences.join(", ")}</span>
          {L.newsletter && <span><b>Newsletter</b>{customer.newsletter ? "Iscritto" : "Non iscritto"}</span>}
          <a className="cust-wa" href={`https://wa.me/${customer.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">Scrivi su WhatsApp</a>
        </div>
        <h4>{mode === "rental" ? "Storico soggiorni" : "Storico prenotazioni"}</h4>
        <ul className="cust-history">{customer.history.map((visit) => <li key={visit.createdAt + visit.time} className={`is-${visit.status}`}>
          <strong>{visit.endDate ? `${it(visit.date)} → ${it(visit.endDate)}${visit.nights ? ` · ${visit.nights} notti` : ""}` : `${it(visit.date)} · ${visit.time}`}</strong>
          <span>{visit.experience} · {visit.partySize} {mode === "rental" ? "ospiti" : "persone"}{visit.details ? ` · ${visit.details}` : ""}</span>
          <em>{STATUS[visit.status]} · {SOURCE[visit.source] || visit.source}</em>
          {visit.notes && <p>«{visit.notes}»</p>}
        </li>)}</ul>
        <button type="button" disabled={!canEdit} onClick={() => exportCustomers([customer], `cliente-${customer.lastName || customer.firstName}-${stamp()}.csv`.replace(/\s+/g, "-"))}>⬇ Scarica la scheda di questo cliente</button>
      </div>}
    </article>)}
  </div>;
}
